import React from 'react';
import { arrayUtils, commonUtils, ListItem } from '@sato/components';
import { observer } from 'mobx-react-lite';

import { useLanguage } from '@hooks/useTranslate';
import { LanguageKey } from '@models/Language';
import { ParkingProduct } from '@models/parking.types';
import { t, TranslatorFn } from '@stores/localization';

/** Helper function to get translated parking space specifications */
const getParkingSpaceSpecifications = (
  {
    parkingSpace,
    parkingArea,
  }: Pick<ParkingProduct, 'parkingSpace' | 'parkingArea'>,
  language: LanguageKey,
  translationFn: TranslatorFn
): { label: string; value: string }[] => {
  const parkingAreaDescription =
    parkingArea?.description?.[language === 'fi' ? 'finnish' : 'english'];

  return [
    {
      label: translationFn('parking.parking_space_type'),
      value: translationFn(`parking.${parkingSpace.type}`),
    },
    parkingSpace.parkingSpaceLevel && {
      label: translationFn('parking.parking_space_level'),
      value: parkingSpace.parkingSpaceLevel,
    },
    (parkingSpace.heatPole || parkingSpace.electricCarCharge) && {
      label: translationFn('parking.parking_space_equipment'),
      value: arrayUtils.joinArrayString([
        parkingSpace.heatPole && translationFn('parking.heatPole'),
        parkingSpace.electricCarCharge &&
          translationFn('parking.electricCarCharge'),
      ]),
    },
    parkingSpace.chargerPlugType && {
      label: translationFn('parking.chargerPlugType'),
      value: translationFn(
        `parking.chargerPlugType.${parkingSpace.chargerPlugType}`
      ),
    },
    parkingSpace.chargerPower && {
      label: translationFn('parking.chargerPower'),
      value: translationFn(
        `parking.chargerPowerType.${parkingSpace.chargerPower}`
      ),
    },
    parkingSpace.spaceSize && {
      label: translationFn('parking.spaceSize'),
      value: parkingSpace.spaceSize,
    },
    parkingArea?.parkingAreaHeating && {
      label: translationFn('parking.parking_area_heating'),
      value: translationFn(`parking.${parkingArea.parkingAreaHeating}`),
    },
    parkingArea?.maximumHeightToDrive && {
      label: translationFn('parking.max_height_for_driving'),
      value: parkingArea?.maximumHeightToDrive,
    },
    parkingArea?.parkingControl && {
      label: translationFn('parking.parking_control'),
      value: translationFn('yes'),
    },
    !!parkingAreaDescription && {
      label: translationFn('parking.parking_area_description'),
      value: parkingAreaDescription,
    },
  ].filter(commonUtils.isTruthy);
};

/** Lists specifications of a parking space */
export const ParkingSpaceSpecificationsList = observer(
  (props: Pick<ParkingProduct, 'parkingSpace' | 'parkingArea'>) => {
    const language = useLanguage();

    const parkingSpaceSpecifications = getParkingSpaceSpecifications(
      props,
      language,
      t
    );

    return (
      <ul>
        {parkingSpaceSpecifications.map((item) => (
          <ListItem key={item.label} horizontal label={item.label}>
            {item.value}
          </ListItem>
        ))}
      </ul>
    );
  }
);
